<template>
  <section class="app-table-wrap sales-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">#</th>
            <th>
              <button
                type="button"
                class="sales-table-sort-btn"
                :title="tableText.sortTitles.date"
                @click="emitSort('sale_date')"
              >
                <span>{{ tableText.columns.date }}</span>
                <i class="fa" :class="sortIconClass('sale_date')" aria-hidden="true"></i>
              </button>
            </th>
            <th v-if="showCustomerColumn">
              <button
                type="button"
                class="sales-table-sort-btn"
                :title="tableText.sortTitles.customer"
                @click="emitSort('customer')"
              >
                <span>{{ tableText.columns.customer }}</span>
                <i class="fa" :class="sortIconClass('customer')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="sales-table-sort-btn"
                :title="tableText.sortTitles.sale"
                @click="emitSort('sale_name')"
              >
                <span>{{ tableText.columns.sale }}</span>
                <i class="fa" :class="sortIconClass('sale_name')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="sales-table-sort-btn"
                :title="tableText.sortTitles.service"
                @click="emitSort('service')"
              >
                <span>{{ tableText.columns.service }}</span>
                <i class="fa" :class="sortIconClass('service')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="sales-table-sort-btn sales-table-sort-btn--right"
                :title="tableText.sortTitles.subtotal"
                @click="emitSort('subtotal')"
              >
                <span>{{ tableText.columns.subtotal }}</span>
                <i class="fa" :class="sortIconClass('subtotal')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="sales-table-sort-btn sales-table-sort-btn--right"
                :title="tableText.sortTitles.balance"
                @click="emitSort('balance')"
              >
                <span>{{ tableText.columns.balance }}</span>
                <i class="fa" :class="sortIconClass('balance')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="sales-status-col">{{ tableText.columns.status }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="columnCount" class="app-table-empty">{{ tableText.loading }}</td>
          </tr>

          <tr v-else-if="normalizedRows.length === 0">
            <td :colspan="columnCount" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in normalizedRows" :key="row.id || index">
            <td class="app-muted">{{ rowNumber(index) }}</td>
            <td>{{ formatDate(row?.sale_date || row?.date) }}</td>
            <td v-if="showCustomerColumn" class="app-strong">
              <a
                v-if="row?.company_id"
                :href="resolveCustomerHref(row)"
                class="sales-table-link"
                :title="tableText.links.customerDetail"
                @click="navigateToCustomerDetail(row, $event)"
              >
                {{ resolveCustomerName(row) }}
              </a>
              <span v-else>{{ resolveCustomerName(row) }}</span>
            </td>
            <td>
              <div class="sales-cell-stack">
                <a
                  :href="resolveSaleHref(row)"
                  class="sales-table-link sales-cell-sub"
                  :title="tableText.links.saleDetail"
                  @click="navigateToSaleDetail(row, $event)"
                >
                  {{ resolveSaleName(row) }}
                </a>
              </div>
            </td>
            <td>{{ resolveServiceName(row) }}</td>
            <td class="app-text-right app-strong">
              <div class="amount-cell">
                <span>{{ formatAmountWithCurrency(row?.subtotal, row?.currency) }}</span>
                <small v-if="isForeignCurrency(row?.currency)" class="amount-cell-exchange-rate">
                  {{ tableText.exchangeRatePrefix }} {{ formatExchangeRate(resolveExchangeRate(row)) }}
                </small>
              </div>
            </td>
            <td class="app-text-right app-strong">
              <div class="amount-cell">
                <span>{{ formatAmountWithCurrency(row?.balance ?? row?.saldo, row?.currency) }}</span>
                <small v-if="isForeignCurrency(row?.currency)" class="amount-cell-exchange-rate">
                  {{ tableText.exchangeRatePrefix }} {{ formatExchangeRate(resolveExchangeRate(row)) }}
                </small>
              </div>
            </td>
            <td class="sales-status-col">
              <span class="app-status-pill" :class="resolveStatusClass(row)">
                {{ resolveStatusLabel(row) }}
              </span>
            </td>

            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--view"
                  :title="tableText.actions.view"
                  @click="navigateToSaleDetail(row)"
                >
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>

                <button
                  v-if="!usesStatusActions(row)"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--edit"
                  :title="tableText.actions.edit"
                  @click="emitEditSale(row)"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>

                <button
                  v-if="usesStatusActions(row)"
                  type="button"
                  class="app-table-icon-btn"
                  :title="tableText.actions.changeStatus"
                  @click="emitChangeSaleStatus(row)"
                >
                  <i class="fa fa-exchange" aria-hidden="true"></i>
                </button>

                <button
                  v-if="usesStatusActions(row)"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete"
                  :title="tableText.actions.delete"
                  @click="emitDeleteSale(row)"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>

                <div v-if="!usesStatusActions(row)" class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="tableText.actions.more"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>

    <teleport to="body">
      <div
        v-if="openRow && !usesStatusActions(openRow)"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button
          type="button"
          @click="emitAction('change-status', openRow)"
        >
          {{ tableText.actions.changeStatus }}
        </button>
        <button
          type="button"
          @click="emitAction('delete', openRow)"
        >
          {{ tableText.actions.delete }}
        </button>
      </div>
    </teleport>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { SALES_TEXT } from '@/texts/catalog/sales';
import { formatDateShort } from '@/utils/datePresentation';
import {
  formatAmountWithCurrency,
  formatExchangeRate as formatExchangeRateValue,
  getRequiredExchangeRate,
  isForeignCurrency,
} from '@/utils/numberFormat';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import { resolveSalesStatusCategory, resolveSalesStatusClass } from '@/composables/shared/useSalesStatus';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  resolveSortIconClass,
  resolveSortTogglePayload,
} from '@/composables/shared/useSorting';

const SHARED_TABLE_TEXT = SALES_TEXT.shared.table;

export default {
  name: 'SharedSalesTable',
  components: {
    AppPagination,
  },
  emits: ['change-sale-status', 'edit-sale', 'delete-sale', 'page-change', 'sort-change'],
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: SALES_TEXT.page.table.empty,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      }),
    },
    showCustomerColumn: {
      type: Boolean,
      default: true,
    },
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
  },
  data() {
    return {
      tableText: SHARED_TABLE_TEXT,
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    normalizedPagination() {
      const raw = this.pagination || {};

      return {
        current_page: Number(raw.current_page || 1),
        per_page: Number(raw.per_page || 30),
        total_pages: Number(raw.total_pages || 1),
        total_items: Number(raw.total_items || 0),
      };
    },
    columnCount() {
      return this.showCustomerColumn ? 9 : 8;
    },
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.normalizedRows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    floatingMenuStyle() {
      return {
        position: 'fixed',
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
        zIndex: 1300,
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.handleGlobalClick, true);
    window.addEventListener('resize', this.closeActionsMenu);
    window.addEventListener('scroll', this.closeActionsMenu, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
    window.removeEventListener('resize', this.closeActionsMenu);
    window.removeEventListener('scroll', this.closeActionsMenu, true);
  },
  methods: {
    formatAmountWithCurrency,
    isForeignCurrency,
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );
    },
    emitSort(field) {
      const nextSort = resolveSortTogglePayload(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );

      if (!nextSort) {
        return;
      }

      this.$emit('sort-change', nextSort);
    },
    rowNumber(index) {
      const currentPage = Number(this.normalizedPagination.current_page || 1);
      const perPage = Number(this.normalizedPagination.per_page || 30);

      return ((currentPage - 1) * perPage) + Number(index) + 1;
    },
    requestPage(page) {
      const nextPage = Number(page || 1);
      if (!Number.isFinite(nextPage)) {
        return;
      }

      if (nextPage < 1 || nextPage > this.normalizedPagination.total_pages) {
        return;
      }

      if (nextPage === this.normalizedPagination.current_page) {
        return;
      }

      this.$emit('page-change', nextPage);
    },
    resolveExchangeRate(row) {
      return getRequiredExchangeRate(row);
    },
    formatExchangeRate(value) {
      return formatExchangeRateValue(value, {
        invalidValue: '-',
      });
    },
    formatDate(value) {
      return formatDateShort(value);
    },
    resolveSaleName(row) {
      return String(row?.sale_name || row?.project || row?.name || row?.code || '').trim() || '-';
    },
    resolveCustomerName(row) {
      return String(row?.company_name || row?.customer_name || row?.cliente || '').trim() || '-';
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', row?.company_id);
    },
    resolveSaleHref(row) {
      return resolveRouteHref(this.$router, 'saleDetail', 'saleId', row?.id);
    },
    navigateToCustomerDetail(row, event = null) {
      navigateToRouteDetail(this.$router, 'customerDetail', 'customerId', row?.company_id, event);
    },
    navigateToSaleDetail(row, event = null) {
      navigateToRouteDetail(this.$router, 'saleDetail', 'saleId', row?.id, event);
    },
    resolveServiceName(row) {
      return String(row?.service_name || row?.service || row?.service_type || '').trim() || '-';
    },
    resolveStatusLabel(row) {
      return String(row?.status_name || row?.status || row?.stage || '').trim() || '-';
    },
    resolveStatusCategory(row) {
      return resolveSalesStatusCategory(row);
    },
    usesStatusActions(row) {
      const category = this.resolveStatusCategory(row);
      return category === 'closed' || category === 'canceled';
    },
    resolveStatusClass(row) {
      return resolveSalesStatusClass(row);
    },
    emitChangeSaleStatus(row) {
      this.$emit('change-sale-status', row);
    },
    emitEditSale(row) {
      this.closeActionsMenu();
      this.$emit('edit-sale', row);
    },
    emitDeleteSale(row) {
      this.closeActionsMenu();
      this.$emit('delete-sale', row);
    },
    toggleActionsMenu(rowId, event) {
      if (!event?.currentTarget) {
        return;
      }

      const nextId = resolveToggledMenuId(this.openActionsMenuId, rowId);
      this.openActionsMenuId = nextId;

      if (!nextId) {
        return;
      }

      const position = computeFloatingMenuPosition(event.currentTarget, 210, 6, 8);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    closeActionsMenu() {
      this.openActionsMenuId = null;
    },
    handleGlobalClick(event) {
      if (!this.openActionsMenuId) {
        return;
      }

      const target = event?.target;
      if (target instanceof Element && target.closest('.app-table-floating-menu')) {
        return;
      }

      if (target instanceof Element && target.closest('.app-table-actions-menu-wrap')) {
        return;
      }

      this.closeActionsMenu();
    },
    emitAction(action, row) {
      this.closeActionsMenu();

      if (action === 'change-status') {
        this.emitChangeSaleStatus(row);
        return;
      }

      if (action === 'delete') {
        this.emitDeleteSale(row);
      }
    },
  },
};
</script>

<style scoped>
.sales-table-wrap {
  margin-top: 14px;
}

.sales-status-col {
  width: 96px;
  text-align: center;
}

.sales-table-link {
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
}

.sales-table-link:hover {
  text-decoration: underline;
}

.sales-table-sort-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.sales-table-sort-btn--right {
  width: 100%;
  justify-content: flex-end;
}

.sales-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sales-cell-sub {
  font-size: 12px;
  line-height: 1.2;
}

.amount-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.amount-cell-exchange-rate {
  font-size: 10px;
  line-height: 1;
  font-weight: 600;
  color: #6b7280;
}
</style>
